import { AnalyticsService } from 'base/analytics';
import { keyOfObject } from 'base/helpers';
import { Button } from 'base/ui/button';
import { IconAdd, IconOption, IconSitemap } from 'base/ui/icons';
import { Inline, SpacerGrow } from 'base/ui/layout';
import { DEFAULT_LOCALE, Locale } from 'base/ui/locales';
import { MenuButton, useRightClickMenu } from 'base/ui/menu';
import { ClickInTextarea, ClickInTextareaRef } from 'base/ui/textarea';
import { TextSmall } from 'base/ui/typography';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { ProjectEditingActions } from '../editing_actions/project';
import { Page, PageState } from '../model';
import { DEFAULT_HOME_SPACE_NAME } from '../model_constants';
import { useSelectable } from '../selection/selectable';
import { SelectionState } from '../selection/states';
import { selectionLocale, SelectionStore } from '../selection/store';
import { SelectionActions } from '../selection_actions';
import { PageListView, showOnPageHoverClassName } from './pages';
import { SectionList as SectionListImpl } from './sections';
import styles from './spaces.css';
import { spaceEditMenu } from './space_common';

export const SitemapSpaces = observer(function _SitemapContent({
  selection,
  selectionActions,
  analytics,
  editing,
  flags,
}: {
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  editing: ProjectEditingActions | undefined;
  flags: FeatureFlagsStore;
}) {
  const SectionList = React.useCallback(
    (props: { page: Page | PageState }) => (
      <SectionListImpl
        {...props}
        selection={selection}
        selectionActions={selectionActions}
        analytics={analytics}
        editing={editing}
        showOnPageHoverClassName={showOnPageHoverClassName}
        flags={flags}
      />
    ),
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection, editing],
  );
  const { project } = selection;
  const { homePage, spaces, homeSpaceName } = project;
  return (
    <div className={styles.spacesWrapper}>
      <div className={styles.spaces}>
        <SpaceTopBar
          locale={selectionLocale({}, project)}
          name={homeSpaceName ?? DEFAULT_HOME_SPACE_NAME}
          selectionTarget={'home'}
          selection={selection}
          projectEditing={editing}
        />
        <PageListView
          SectionList={SectionList}
          editing={editing}
          selection={selection}
          selectionActions={selectionActions}
          analytics={analytics}
          pages={[homePage]}
          parent={undefined}
          flags={flags}
          locale={selectionLocale({}, project)}
        />
        {spaces.map((space) => (
          <React.Fragment key={keyOfObject(space)}>
            <SpaceTopBar
              locale={selectionLocale({ space }, project)}
              name={space.name}
              selectionTarget={space}
              selection={selection}
              projectEditing={editing}
            />
            <PageListView
              SectionList={SectionList}
              editing={editing}
              selection={selection}
              selectionActions={selectionActions}
              analytics={analytics}
              pages={space.subPages}
              parent={space}
              locale={selectionLocale({ space }, project)}
              flags={flags}
            />
          </React.Fragment>
        ))}
        {editing && <AddSpace editing={editing} analytics={analytics} />}
      </div>
    </div>
  );
});

function AddSpace({
  editing,
  analytics,
}: {
  editing: ProjectEditingActions;
  analytics: AnalyticsService;
}) {
  const onClick = () => {
    editing.addSpaceToEnd();
    analytics.track({
      event: 'blocks/add-space',
      method: 'blank',
    });
  };

  return (
    <div className={styles.headerWrapper}>
      <div className={styles.headerLine}>
        <div className={styles.headerLineCenter}>
          <Button icon={IconAdd} variant="subtle" onClick={onClick} />
        </div>
      </div>
    </div>
  );
}

const SpaceTopBar = observer(function _SpaceTopBar({
  name,
  locale,
  selectionTarget,
  selection,
  projectEditing,
}: {
  name: string;
  locale: Locale;
  selectionTarget: Exclude<SelectionState['space'], undefined>;
  selection: SelectionStore;
  projectEditing: ProjectEditingActions | undefined;
}) {
  const topBarRef = React.useRef<HTMLDivElement>(null);
  const nameRef = React.useRef<ClickInTextareaRef>(null);

  const editing = projectEditing?.forSpace(selectionTarget);
  const menu =
    projectEditing && editing ? spaceEditMenu(projectEditing, editing) : [];
  const [onContextMenu, rightClickMenu] = useRightClickMenu(menu);

  const [isSelected, selectableClassName] = useSelectable(
    topBarRef,
    selection,
    { space: selectionTarget },
    {
      onSelect: projectEditing?.sidebarNavigation.openEditPanelIfClosed,
      hideFrame: true,
    },
  );
  useKeyboardShortcuts(isSelected, {
    [shortcut('Enter')]: () => nameRef.current?.focus(),
  });

  return (
    <div
      className={classNames(styles.headerWrapper, styles.withBottomPadding)}
      onContextMenu={onContextMenu}
    >
      <div
        className={classNames(
          styles.topBar,
          selectableClassName,
          isSelected && styles.isSelected,
        )}
        ref={topBarRef}
      >
        <Inline spacing={8} yalign="center">
          <div className={styles.topBarIcon}>
            <IconSitemap />
          </div>
          <TextSmall>
            <ClickInTextarea
              ref={nameRef}
              lang={locale || DEFAULT_LOCALE}
              value={name}
              placeholder="Untitled Sitemap"
              onChange={editing?.setName}
              enableClickIn={true}
              width="content"
            />
          </TextSmall>
          <SpacerGrow />
          {menu.length > 0 && (
            <MenuButton icon={IconOption} variant="tertiary" menu={menu} />
          )}
        </Inline>
        {rightClickMenu}
      </div>
    </div>
  );
});
